'use client';

import { Form } from '@/components/ui/form';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  FormError,
  FormSuccess,
  FormInput,
  FormSelect,
} from '@/components/form';
import { ExtendedUser } from '@/types/next-auth';
import { useMultiStepForm } from '@/hooks/useMultiStepForm';
import { LogoUpload } from './logo-upload';
import { uploadLogo } from '@/actions';
import { useState, useCallback } from 'react';
import { Loader2 } from 'lucide-react';
import * as z from 'zod';
import { AccountSettingsSchema } from '@/schemas';
import { useTranslations } from '@/hooks/useTranslations';
import { userCan } from '@/lib/plans';
import { FeatureToggleRow } from './_shared/feature-toggle-row';

interface AccountSettingsFormProps {
  user: ExtendedUser;
  redirectOnSuccess?: string;
  onFormComplete?: (values: z.infer<typeof AccountSettingsSchema>) => void;
  onSkip?: (values: z.infer<typeof AccountSettingsSchema>) => void;
  onUpgradeClick?: (
    values: z.infer<typeof AccountSettingsSchema>
  ) => Promise<void>;
  hideInvoicePrefix?: boolean;
}

const STEP_LABELS = [
  'personalInfo',
  'addressInfo',
  'companyInfo',
  'bankDetails',
  'features',
] as const;

// Move ClassicStepIndicator outside
interface ClassicStepIndicatorProps {
  totalSteps: number;
  currentStep: number;
  goToStep: (step: number) => void;
  t: (key: string) => string;
}

const ClassicStepIndicator = ({
  totalSteps,
  currentStep,
  goToStep,
  t,
}: ClassicStepIndicatorProps) => (
  <div className="flex justify-center items-center space-x-2 py-10">
    {Array.from({ length: totalSteps }, (_, i) => i + 1).map((step, index) => (
      <div key={step} className="flex items-center">
        <div
          className={`w-10 h-10 flex items-center justify-center rounded-full cursor-pointer font-semibold transition-all duration-200 hover:scale-105 ${
            currentStep === step
              ? 'bg-primary text-primary-foreground shadow-lg'
              : currentStep > step
                ? 'bg-green-500 text-white'
                : 'bg-secondary hover:bg-secondary/80'
          }`}
          onClick={() => goToStep(step)}
          role="button"
          tabIndex={0}
          aria-label={`Go to step ${step}: ${t(STEP_LABELS[index])}`}
        >
          {currentStep > step ? '✓' : step}
        </div>
        {index < totalSteps - 1 && (
          <div
            className={`w-8 h-0.5 mx-2 ${currentStep > step ? 'bg-green-500' : 'bg-secondary'}`}
          />
        )}
      </div>
    ))}
  </div>
);

// Move NavigationButtons outside
interface NavigationButtonsProps {
  currentStep: number;
  totalSteps: number;
  isPending: boolean;
  prevStep: () => void;
  nextStep: () => void;
  common: (key: string) => string;
}

const NavigationButtons = ({
  currentStep,
  totalSteps,
  isPending,
  prevStep,
  nextStep,
  common,
}: NavigationButtonsProps) => (
  <div className="flex gap-3 pt-4">
    {currentStep > 1 && (
      <Button
        type="button"
        variant="outline"
        onClick={prevStep}
        className="flex-1"
        disabled={isPending}
        aria-label="Previous"
      >
        {common('previous')}
      </Button>
    )}
    {currentStep < totalSteps && (
      <Button
        type="button"
        onClick={nextStep}
        className="flex-1"
        disabled={isPending}
        aria-label="Next"
      >
        {common('next')}
      </Button>
    )}
    {currentStep === totalSteps && (
      <Button
        type="submit"
        className="flex-1"
        disabled={isPending}
        aria-label="Submit"
      >
        {isPending ? common('saving') || 'Saving...' : common('confirm')}
      </Button>
    )}
  </div>
);

export function AccountSettingsForm({
  user,
  redirectOnSuccess = '/dashboard',
  onFormComplete,
  onSkip,
  onUpgradeClick,
  hideInvoicePrefix = false,
}: AccountSettingsFormProps) {
  const translations = useTranslations('UserSettings');
  const {
    form,
    error,
    success,
    isPending,
    currentStep,
    totalSteps,
    progress,
    nextStep,
    prevStep,
    goToStep,
    onSubmit: originalOnSubmit,
    currencyOptions,
    t,
    common,
  } = useMultiStepForm({ user, redirectOnSuccess });

  // Wrap onSubmit to call onFormComplete if provided
  const onSubmit = async (values: z.infer<typeof AccountSettingsSchema>) => {
    if (onFormComplete) {
      onFormComplete(values);
    } else {
      await originalOnSubmit(values);
    }
  };

  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoUrl, setLogoUrl] = useState<string | null>(user.logo || null);
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);
  const [upgrading, setUpgrading] = useState(false);

  const handleUpgradeClick = onUpgradeClick
    ? async () => {
        if (upgrading) return;
        setUpgrading(true);
        try {
          await onUpgradeClick(form.getValues());
        } finally {
          setUpgrading(false);
        }
      }
    : undefined;

  const handleLogoSelect = useCallback(
    async (file: File) => {
      setLogoFile(file);
      setIsUploadingLogo(true);

      try {
        // Upload file first
        const formData = new FormData();
        formData.append('file', file);

        const uploadResult = await uploadLogo(formData);
        if (uploadResult.error) {
          // Set form error if there's an upload error
          return;
        }

        if (uploadResult.success) {
          setLogoUrl(uploadResult.success);
          form.setValue('logo', uploadResult.success);
        }
      } catch (err) {
        console.error('Logo upload failed:', err);
      } finally {
        setIsUploadingLogo(false);
      }
    },
    [form]
  );

  const handleRemoveLogo = useCallback(() => {
    setLogoFile(null);
    setLogoUrl(null);
    form.setValue('logo', '');
  }, [form]);

  const renderStepContent = (step: number) => {
    switch (step) {
      case 1:
        return (
          <>
            <div className="flex flex-col sm:flex-row gap-4">
              <FormInput
                control={form.control}
                name="name"
                label={common('name')}
                placeholder="John Doe"
                disabled={isPending}
              />
              {!user?.isOAuth && (
                <FormInput
                  control={form.control}
                  name="email"
                  label={t('email')}
                  placeholder="john.doe@example.com"
                  type="email"
                  disabled={isPending}
                />
              )}
            </div>
            <div className="flex flex-col sm:flex-row gap-4">
              <FormInput
                control={form.control}
                name="phone"
                label={t('phone')}
                placeholder="+123 45 678 900"
                disabled={isPending}
              />
              <FormSelect
                control={form.control}
                name="currency"
                label={t('currency')}
                options={currencyOptions}
                placeholder={t('currencyPlaceholder')}
                disabled={isPending}
              />
            </div>
          </>
        );
      case 2:
        return (
          <>
            <div className="flex flex-col sm:flex-row gap-4">
              <FormInput
                control={form.control}
                name="companyAddress"
                label={t('companyAddress')}
                placeholder="Hauptstrasse 15, Berlin"
                disabled={isPending}
              />
              <FormInput
                control={form.control}
                name="zipCode"
                label={t('zipCode')}
                placeholder="10115"
                disabled={isPending}
              />
            </div>
            <div className="flex flex-col sm:flex-row gap-4">
              <FormInput
                control={form.control}
                name="city"
                label={t('city')}
                placeholder="Berlin"
                disabled={isPending}
              />
              <FormInput
                control={form.control}
                name="country"
                label={t('country')}
                placeholder="Germany"
                disabled={isPending}
              />
            </div>
          </>
        );
      case 3:
        return (
          <>
            <div className="flex flex-col sm:flex-row gap-4">
              <FormInput
                control={form.control}
                name="field"
                label={t('fieldOfOperation')}
                placeholder="Technology Services"
                disabled={isPending}
              />
              <FormInput
                control={form.control}
                name="companyName"
                label={t('companyName')}
                placeholder="Tech Solutions GmbH"
                disabled={isPending}
              />
            </div>
            <div className="flex flex-col sm:flex-row gap-4">
              <FormInput
                control={form.control}
                name="taxId"
                label={t('taxId')}
                placeholder="DE123456789"
                disabled={isPending}
              />
              <FormInput
                control={form.control}
                name="registrationNumber"
                label={t('registrationNumber')}
                placeholder="HRB 12345"
                disabled={isPending}
              />
            </div>
            {!hideInvoicePrefix && (
              <div className="flex flex-col sm:flex-row gap-4">
                <FormInput
                  control={form.control}
                  name="invoiceNumberPrefix"
                  label={t('invoiceNumberPrefix')}
                  placeholder="ACME-"
                  disabled={isPending}
                />
              </div>
            )}
            <div className="mt-4">
              <LogoUpload
                onFileSelect={handleLogoSelect}
                currentFile={logoFile}
                currentUrl={logoUrl}
                onRemove={handleRemoveLogo}
                disabled={isPending || isUploadingLogo}
              />
              {isUploadingLogo && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground mt-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span>Uploading logo...</span>
                </div>
              )}
            </div>
          </>
        );
      case 4:
        return (
          <>
            <div className="flex flex-col sm:flex-row gap-4">
              <FormInput
                control={form.control}
                name="bankName"
                label={t('bankName')}
                placeholder="Bank of Europe"
                disabled={isPending}
              />
              <FormInput
                control={form.control}
                name="accountNumber"
                label={t('accountNumber')}
                placeholder="123456789"
                disabled={isPending}
              />
            </div>
            <div className="flex flex-col sm:flex-row gap-4">
              <FormInput
                control={form.control}
                name="bic"
                label={t('bic')}
                placeholder="BOFAUS3N"
                disabled={isPending}
              />
            </div>
          </>
        );
      case 5: {
        const locked = !userCan(user, 'expenses');
        return (
          <FeatureToggleRow
            id="enableExpenses"
            title={t('enableExpenses')}
            description={t('enableExpensesDescription')}
            checked={!!form.watch('enableExpenses')}
            onCheckedChange={(checked) =>
              form.setValue('enableExpenses', checked)
            }
            disabled={isPending}
            locked={locked}
            lockedBadge={t('proOnlyBadge')}
            lockedLinkLabel={t('upgradeToPro')}
            onLockedClick={handleUpgradeClick}
            lockedPending={upgrading}
          />
        );
      }
      default:
        return null;
    }
  };

  return (
    <div className="w-full max-w-4xl">
      <Card>
        <CardHeader>
          <CardTitle>{translations('myDetails')}</CardTitle>
          <CardDescription>
            {translations('description') ||
              'Update your personal information, company details, and account settings'}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
              <ClassicStepIndicator
                totalSteps={totalSteps}
                currentStep={currentStep}
                goToStep={goToStep}
                t={t}
              />

              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-base">
                    {t(STEP_LABELS[currentStep - 1])}
                  </h3>
                  <span className="text-sm text-muted-foreground">
                    Step {currentStep} of {totalSteps}
                  </span>
                </div>

                <div className="w-full bg-secondary rounded-full h-2">
                  <div
                    className="bg-primary h-2 rounded-full transition-all duration-300 ease-out"
                    style={{ width: `${progress}%` }}
                  />
                </div>

                <div className="space-y-4 pt-2" key={currentStep}>
                  {renderStepContent(currentStep)}
                </div>
              </div>

              <div className="space-y-2">
                <FormError message={error} />
                <FormSuccess message={success} />
              </div>

              <NavigationButtons
                currentStep={currentStep}
                totalSteps={totalSteps}
                isPending={isPending}
                prevStep={prevStep}
                nextStep={nextStep}
                common={common}
              />
            </form>
          </Form>

          {onSkip && (
            <div className="text-center mt-4">
              <button
                type="button"
                onClick={() => onSkip(form.getValues())}
                className="text-sm text-muted-foreground hover:text-foreground transition-colors underline underline-offset-4"
              >
                Skip for now →
              </button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
